import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, useWindowDimensions } from 'react-native';
import { BoxStyleInfo, FormatInfo, LookupChoiceForStep } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { neutrals } from 'src/theme/colors';
import i18n from 'src/app/i18n';
import { useDebounceCallback } from 'src/hooks/useDebounceCallback';
import { GROUP_CODES } from 'src/constants/code.constants';
import { getFormattedPrice } from 'src/helpers/price.helper';

const DEFAULT_FORMAT_SUGGESTED = {
  [GROUP_CODES.PRINT_CLASSIC]: '12x00',
  [GROUP_CODES.PRINT_CLASSIC_BORDER]: '12x16',
  [GROUP_CODES.POSTER]: '12x00',
  [GROUP_CODES.PRINT_VINTAGE]: '12x00',
  [GROUP_CODES.PRINT_PRO]: '7x10',
  [GROUP_CODES.POSTER_PRO]: '18x24',
  [GROUP_CODES.HAPPY_CASE_CLASSIC]: '12x00',
  [GROUP_CODES.HAPPY_CASE_VINTAGE]: '12x00',
  [GROUP_CODES.FOTOKIT_HOMEDECOR]: '12x00',
  [GROUP_CODES.FOTOKIT_MINISCRAP]: '12x00',
};
const ITEM_SPACING = 16;
const FEW_ITEMS_THRESHOLD = 3;

interface UseOptionStepParams {
  cards: FormatInfo[] | BoxStyleInfo[] | LookupChoiceForStep[];
  selectedCard?: string;
  setSelectedCard: (code: string) => void;
  type: 'FormatInfo' | 'BoxStyleInfo' | 'LookupChoiceForStep' | 'PaperType' | 'PaperFinish';
  onConfirmStep: () => void;
  groupCode?: string;
}

export const useOptionStep = ({
  cards,
  selectedCard,
  setSelectedCard,
  type,
  onConfirmStep,
  groupCode,
}: UseOptionStepParams) => {
  const { width, height } = useWindowDimensions();
  const { isDesktop, isTablet } = useResponsiveScreen();

  const flatListRef = useRef<FlatList<FormatInfo | LookupChoiceForStep>>(null);
  const isFormat = type === 'FormatInfo';
  const isLookupChoice = type === 'PaperType' || type === 'PaperFinish';
  const isFewItems = cards.length <= FEW_ITEMS_THRESHOLD;

  const widthSize = width * (isDesktop ? (isFewItems ? 0.26 : 0.15) : isFewItems ? 0.8 : 0.4);
  const ITEM_FULL_HEIGHT = isDesktop || isTablet ? height * 0.4 : Math.max(height * 0.35, 300);
  const numColumns = isDesktop ? 1 : isFewItems ? 1 : 2;

  const NORMAL_DESKTOP_WIDTH_RATIO = 0.15;
  const NORMAL_MOBILE_WIDTH_RATIO = 0.52;
  const IMAGE_ASPECT_RATIO = 1.15;
  const imageHeight = isFewItems
    ? isDesktop
      ? (width * NORMAL_DESKTOP_WIDTH_RATIO) / IMAGE_ASPECT_RATIO
      : (width * NORMAL_MOBILE_WIDTH_RATIO) / IMAGE_ASPECT_RATIO
    : undefined;
  const ITEM_FULL_WIDTH = widthSize + ITEM_SPACING;

  const sidePadding =
    isFewItems && isDesktop
      ? (width - (cards.length * widthSize + (cards.length - 1) * ITEM_SPACING)) / 2
      : (width - widthSize) / 2 - ITEM_SPACING * 2;

  const checkSuggestedCard = useCallback(
    (
      item: FormatInfo | BoxStyleInfo | LookupChoiceForStep,
      index: number,
      array: Array<FormatInfo | BoxStyleInfo | LookupChoiceForStep>,
    ) => {
      if ('isDefault' in item) {
        return item.isDefault;
      } else if ('isSuggested' in item && array.some(it => 'isSuggested' in it && it.isSuggested)) {
        return item.isSuggested;
      } else if ('code' in item && isFormat) {
        return (
          groupCode &&
          groupCode in DEFAULT_FORMAT_SUGGESTED &&
          item.code === DEFAULT_FORMAT_SUGGESTED[groupCode as GROUP_CODES]
        );
      } else {
        return index === 0;
      }
    },
    [isFormat, groupCode],
  );

  const suggested = useMemo(() => cards.find(checkSuggestedCard), [cards, checkSuggestedCard]);

  const checkSelectedCard = useCallback(
    (item: FormatInfo | BoxStyleInfo | LookupChoiceForStep, index: number) => {
      return selectedCard
        ? item.productConfigurationId === selectedCard
        : suggested
          ? item.productConfigurationId === suggested.productConfigurationId
          : index === 0;
    },
    [selectedCard, suggested],
  );

  const { selected, selectedIndex } = useMemo(() => {
    const index = cards.findIndex(checkSelectedCard);

    return {
      selected: cards[index],
      selectedIndex: index,
    };
  }, [cards, checkSelectedCard]);

  const [activeIndex, setActiveIndex] = useState<number>(selectedIndex);

  useEffect(() => {
    if (selected) {
      setSelectedCard(selected.productConfigurationId);
    }
  }, []);

  useEffect(() => {
    if (isFewItems) return;
    if (!flatListRef.current || selectedIndex < 0) return;

    if (isDesktop) {
      flatListRef.current.scrollToIndex({
        index: selectedIndex,
        animated: true,
      });
    } else {
      const rowIndex = Math.floor(selectedIndex / numColumns);
      const offset = rowIndex * ITEM_FULL_HEIGHT - height / 2 + ITEM_FULL_HEIGHT / 2;

      flatListRef.current.scrollToOffset({
        offset,
        animated: true,
      });
    }
  }, [selectedIndex, isDesktop, isFewItems]);

  const renderItem = useCallback(
    ({ item, index }: { item: FormatInfo | BoxStyleInfo | LookupChoiceForStep; index: number }) => {
      const getTypeSpecificProps = () => {
        if (isLookupChoice) {
          const lookupItem = item as LookupChoiceForStep;
          const lookupKey = lookupItem.name.toLowerCase();
          const typeKey = type.toLowerCase();

          return {
            code: lookupItem.productConfigurationId,
            previewImageUrl: lookupItem.imageUrl,
            title: i18n.t(`modal-configuration-steps:${typeKey}:${lookupKey}`),
            description: i18n.t(`modal-configuration-steps:${typeKey}-content:${lookupKey}`),
            isSuggested: lookupItem.isDefault,
            ...(type === 'PaperType' && {
              textSuggested: 'modal-configuration-steps:suggested-lookup',
            }),
          };
        }

        if (isFormat) {
          const formatItem = item as FormatInfo;
          const formattedCode = formatItem.code?.split('.')?.[0];
          const isSuggestedItem = formatItem.productConfigurationId === suggested?.productConfigurationId;

          return {
            code: formatItem.code,
            previewImageUrl: formatItem.previewImageUrl,
            title: i18n.t(`modal-configuration-steps:format:${formattedCode}`),
            description: i18n.t(`modal-configuration-steps:formats-content:${groupCode}:${formattedCode}`),
            price: i18n.t('discounts-table:price-discount', {
              price: getFormattedPrice({
                price: (isFormat ? item?.minPrice : item?.price) || 0,
                withCurrency: false,
              }),
            }),
            isSuggested: isSuggestedItem,
          };
        }

        // BoxStyle
        const boxItem = item as BoxStyleInfo;
        return {
          code: boxItem.code,
          previewImageUrl: boxItem.previewUrl,
          title: i18n.t(`modal-configuration-steps:box-style:${boxItem.code?.toLocaleLowerCase()}`),
        };
      };

      return {
        ...getTypeSpecificProps(),
        index,
        isSelected: item.productConfigurationId === selected?.productConfigurationId,
        width: widthSize,
        borderColor: isDesktop ? `${neutrals.white}00` : neutrals.neutralGrays.dust,
        onPress: () => setSelectedCard(item.productConfigurationId),
      };
    },
    [selected, suggested, widthSize, isDesktop, isLookupChoice],
  );

  const getItemLayout = useCallback(
    (_: ArrayLike<FormatInfo> | null | undefined, index: number) => {
      if (isDesktop) {
        return {
          length: ITEM_FULL_WIDTH,
          offset: ITEM_FULL_WIDTH * index,
          index,
        };
      }
      const row = Math.floor(index / numColumns);
      return {
        length: ITEM_FULL_HEIGHT,
        offset: ITEM_FULL_HEIGHT * row,
        index,
      };
    },
    [isDesktop, numColumns, ITEM_FULL_WIDTH, ITEM_FULL_HEIGHT],
  );

  const scrollToIndex = useCallback(
    (index: number, animated = false) => {
      setActiveIndex(prev => {
        if (index >= 0 && index < cards.length) {
          flatListRef?.current?.scrollToIndex({ index, animated });
          return index;
        }
        return prev;
      });
    },
    [cards.length],
  );

  useEffect(() => {
    if (!isDesktop) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = activeIndex + 1;
        if (nextIndex < cards.length) {
          scrollToIndex(nextIndex);
          const item = cards[nextIndex];
          if (item.productConfigurationId !== selectedCard) {
            setSelectedCard(item.productConfigurationId);
          }
        }
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const nextIndex = activeIndex - 1;
        if (nextIndex >= 0) {
          scrollToIndex(nextIndex);
          let item = cards[nextIndex];
          if (item.productConfigurationId !== selectedCard) {
            setSelectedCard(item.productConfigurationId);
          }
        }
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        onConfirmStep();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDesktop, activeIndex, selectedCard, scrollToIndex]);

  const onScrollHandler = useDebounceCallback(e => {
    if (!isDesktop || isFewItems) return;

    const offsetX = e.nativeEvent.contentOffset.x;
    const index = Math.round(offsetX / ITEM_FULL_WIDTH);
    scrollToIndex(index, true);
  }, 100);

  const isPoster = groupCode === GROUP_CODES.POSTER || groupCode === GROUP_CODES.POSTER_PRO;

  return {
    isDesktop,
    isFewItems,
    numColumns,
    flatListRef,
    suggested,
    ITEM_SPACING,
    sidePadding,
    renderItem,
    getItemLayout,
    scrollToIndex,
    activeIndex,
    onScrollHandler,
    ITEM_FULL_HEIGHT,
    isFormat,
    isPoster,
    imageHeight,
  };
};
